import * as React from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SortDirection } from "@/shared/lib/sort";

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  ),
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />,
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
  ),
);
TableBody.displayName = "TableBody";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn("border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/50", className)}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className)} {...props} />
  ),
);
TableCell.displayName = "TableCell";

/**
 * A column header that sorts, as an appearance only: which direction a click asks for,
 * and whether the click reorders rows already held or fetches a new page, stays with the
 * table that owns the data. The blotter sorts on the server and this affordance is also
 * used by the positions table, which sorts in the browser — one look, two behaviours.
 */
const SortableTableHead = ({
  label,
  direction,
  onSort,
  testId,
  disabled,
  align = "left",
}: {
  label: string;
  direction: SortDirection | null;
  onSort: () => void;
  testId: string;
  disabled?: boolean;
  align?: "left" | "right";
}) => (
  <TableHead
    aria-sort={direction ?? "none"}
    className={align === "right" ? "text-right" : undefined}
  >
    <button
      type="button"
      disabled={disabled}
      className={cn(
        "flex items-center gap-1 hover:text-foreground disabled:opacity-60",
        align === "right" && "w-full justify-end",
      )}
      onClick={onSort}
      data-testid={testId}
    >
      {label}
      {direction === "descending" && <ArrowDown className="h-3 w-3" />}
      {direction === "ascending" && <ArrowUp className="h-3 w-3" />}
    </button>
  </TableHead>
);
export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, SortableTableHead };
